import React, { useState, FunctionComponent, ChangeEvent } from 'react';

import { update as updateResource } from '@api/v4/resources/resources';

import InputContainer from '@components/library/input_container/InputContainer';
import { InputType } from '@components/library/inputs/Input.props';
import { PrimaryButton } from '@components/library/button/index';

import './styles/_download-alert-setting.scss';

interface DownloadAlertSettingProps {
  downloadAlert: string;
  downloadAlertEnabled: boolean;
}

const DownloadAlertSetting: FunctionComponent<DownloadAlertSettingProps> = ({ downloadAlert, downloadAlertEnabled }) => {
  // we need the "initial" state variables in the case where the user edits the setting more than once without
  // closing the settings modal - that is, we can't rely on the props provided to change and trigger a rerender
  const [initialDownloadAlert, setInitialDownloadAlert] = useState(downloadAlert);
  const [initialDownloadAlertEnabled, setInitialDownloadAlertEnabled] = useState(downloadAlertEnabled);
  const [alertEnabled, setAlertEnabled] = useState(downloadAlertEnabled);
  const [downloadAlertInput, setDownloadAlertInput] = useState(downloadAlert);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const notifySuccess = (): void => Notify.create({
    title: bfTranslate('Download alert setting updated!'),
    type: "success"
  });

  const notifyFailure = (): void => Notify.create({
    title: bfTranslate('Error updating download alert setting. Please contact support.'),
    type: "error"
  });

  const submitDisableDownloadAlert = async (): Promise<void> => {
    try {
      await updateResource({
        key: BFG?.resource?.key,
        resourceType: BFG?.resource?.type,
        downloadAlertEnabled: false
      });

      notifySuccess();
      setInitialDownloadAlertEnabled(false);
    } catch {
      notifyFailure();
    }
  };

  const submitDownloadAlertUpdate = async (): Promise<void> => {
    try {
      await updateResource({
        key: BFG?.resource?.key,
        resourceType: BFG?.resource?.type,
        downloadAlert: downloadAlertInput !== initialDownloadAlert ? downloadAlertInput : undefined,
        downloadAlertEnabled: alertEnabled !== initialDownloadAlertEnabled ? alertEnabled : undefined
      });

      if (downloadAlertInput !== initialDownloadAlert) {
        setInitialDownloadAlert(downloadAlertInput);
      }
      if (alertEnabled !== initialDownloadAlertEnabled) {
        setInitialDownloadAlertEnabled(alertEnabled);
      }
      setIsSubmitting(false);
      notifySuccess();
    } catch {
      notifyFailure();
    }
  };

  return (
    <div className="download-alert-setting-container">
      <div className="advanced-tab-settings__row">
        <div className="quarter-column">
          <p>{bfTranslate('Download Alert')}</p>
        </div>
        <div className="three-quarters-column">
          <div className="advanced-tab-settings__options">
            <div className="radio-button-container">
              <input
                checked={alertEnabled}
                className="radio-margin"
                id="download_alert_enabled"
                name="download_alert"
                onChange={(): void => { setAlertEnabled(true); }}
                type="radio"
                value="true"
              />
              <label htmlFor="download_alert_enabled">{bfTranslate('Enabled')}</label>
            </div>
            <div className="radio-button-container">
              <input
                checked={!alertEnabled}
                className="radio-margin"
                id="download_alert_disabled"
                name="download_alert"
                onChange={(): void => {
                  setAlertEnabled(false);
                  if (initialDownloadAlertEnabled) {
                    submitDisableDownloadAlert();
                  }
                }}
                type="radio"
                value="false"
              />
              <label htmlFor="download_alert_disabled">{bfTranslate('Disabled')}</label>
            </div>
          </div>
        </div>
      </div>
      {alertEnabled && (
        <div className="download-alert-update__container">
          <InputContainer
            attributes={{
              className: "download-alert-textarea",
              name: "download-alert",
              onChange: (e: ChangeEvent<HTMLTextAreaElement>): void => {
                setDownloadAlertInput(e.target.value);
              },
              type: InputType.Textarea,
            }}
            input={{ value: downloadAlertInput, error: '' }}
            labelCopy={bfTranslate("Download Alert Message")}
          />
          <PrimaryButton
            className="download-alert-update__submit"
            disabled={downloadAlertInput === initialDownloadAlert && alertEnabled === initialDownloadAlertEnabled}
            isLoading={isSubmitting}
            loadingCopy={bfTranslate('Updating')}
            onClick={(): void => {
              setIsSubmitting(true);
              submitDownloadAlertUpdate();
            }}
            size="medium"
          >
            {bfTranslate('Update')}
          </PrimaryButton>
        </div>
      )}
    </div>
  );
};

export default DownloadAlertSetting;
